import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Carousel from './Carousel';
import Footer from './Footer';

gsap.registerPlugin(ScrollTrigger);

export default function HomePage() {
  const historyRef = useRef(null);
  const privateRef = useRef(null);
  const historyImages = useRef([]);

  useEffect(() => {
    // Direct ScrollTrigger without matchMedia for simplicity
    const sections = [
      { el: historyRef.current, scale: 0.9, y: -100 },
      { el: privateRef.current, scale: 0.9, y: -100 },
    ];

    sections.forEach(({ el, scale, y }) => {
      if (!el) return;
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: 'bottom bottom',
          end: '+=100%',
          pin: true,
          pinSpacing: false,
          scrub: 1,
          anticipatePin: 1,
        },
      });
      tl.to(el, { scale, y, alpha: 0, ease: 'none' });
    });

    historyImages.current.forEach((img) => {
      if (!img) return;
      gsap.fromTo(
        img,
        { scale: 0.2, transformOrigin: 'left center' },
        {
          scale: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: img,
            start: 'top bottom',
            end: 'bottom center',
            scrub: 1,
          },
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <div className="home-slideshow">
        <section className="slice-home-slideshow">
          <div className="slice-home-introduction">
            <div className="slice-home-introduction-header">
              <h2 style={{ fontWeight: 700, fontSize: '2.0rem', lineHeight: 1.3, color: '#fff' }}>
                Місце, де оживає історія, зберігається пам'ять поколінь і відкривається культурна спадщина Городоччини.
              </h2>
            </div>
            <div className="slice-home-introduction-inner">
              <p style={{ fontSize: '1.15rem', lineHeight: 1.6, color: '#ffffff' }}>
                Це офіційний інформаційний ресурс Городоцького краєзнавчого музею G-Museum.
              </p>
              <p style={{ fontSize: '1.15rem', lineHeight: 1.6, color: '#ffffff' }}>
                Тут ви зможете ознайомитися з історією музею, дізнатися про його експозиції, актуальні події та години роботи, а також зручно придбати квитки онлайн для планування свого візиту.
              </p>
            </div>
          </div>
          <Carousel />
        </section>
      </div>

      <section id="istoriya" className="home-history" ref={historyRef}>
        <div className="slice-home-history">
          <div className="slice-home-history-left">
            <div className="slice-home-history-item">
              <div className="slice-home-history-item-image">
                <img src="/assets/img/mus1.jpg" alt="" />
              </div>
            </div>
            <div className="slice-home-history-item">
              <div className="slice-home-history-item-image">
                <img src="/assets/img/mus2.jpg" alt="" />
              </div>
            </div>
            <div className="slice-home-history-item">
              <div className="slice-home-history-item-image">
                <img src="/assets/img/mus3.jpg" alt="" />
              </div>
            </div>
            <div className="slice-home-history-item">
              <div className="slice-home-history-item-image">
                <img src="/assets/img/mus4.jpg" alt="" />
              </div>
            </div>
            <div className="slice-home-history-item">
              <div className="slice-home-history-item-image">
                <img src="/assets/img/mus5.jpg" alt="" />
              </div>
            </div>
          </div>
          <div className="slice-home-history-right">
            <div className="slice-home-history-right-small-title">
              <h2>Історія G-Museum</h2>
            </div>
            <div className="slice-home-history-right-large-title">
              <h2>Від краєзнавчого музею до сучасного центру історії, науки та культури</h2>
            </div>
            <div className="slice-home-history-right-paragraph">
              <p>
                Історія Городоцького краєзнавчого музею розпочалася 6 червня 1969 року. Створений завдяки зусиллям місцевих краєзнавців та ентузіастів, музей протягом десятиліть зберігав історичну й культурну спадщину Городоччини, археологічні знахідки, історичні документи та унікальні свідчення минулого.
              </p>
              <p>
                Нову сторінку в історії музею відкрили меценати Микола Іванович та Галина Гуменюк. Завдяки їхній ініціативі, фінансовій підтримці та прагненню зберегти культурну спадщину рідного краю стало можливим масштабне відродження музею. Особливу роль у реалізації проєкту відіграв Микола Гуменюк — уродженець Городоччини, професор медицини та засновник фармацевтичної корпорації «ЮРіЯ-ФАРМ».
              </p>
              <p>
                У 2016 році розпочалася масштабна реконструкція, яка перетворила старий краєзнавчий музей на сучасний культурно-освітній комплекс. 29 травня 2021 року відбулося урочисте відкриття оновленого G-Museum, що став справжнім подарунком меценатів Городоцькій громаді.
              </p>
              <p>
                Особливе місце в музеї посідає спадщина Сергія Виноградського — видатного українського вченого, одного із засновників екологічної мікробіології, життя якого було тісно пов'язане з Городком. Окрема експозиція знайомить відвідувачів із його життям та науковими відкриттями світового значення.
              </p>
              <p>
                Сьогодні G-Museum — це сучасний простір історії, культури, науки та освіти, де багатовікова спадщина Поділля поєднується з інноваційними технологіями.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="godini" className="home-private-events" ref={privateRef}>
        <div className="slice-home-private-events">
          <div className="slice-home-private-events-left">
            <img src="/assets/img/IMG_9501.jpg" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div className="slice-home-private-events-right">
            <div className="slice-home-private-events-right-small-title">
              <h2>Години роботи музею</h2>
            </div>
            <div className="slice-home-private-events-right-large-title">
              <h2>Запрошуємо відвідати Городоцький краєзнавчий музей G-Museum!</h2>
            </div>
            <div className="slice-home-private-events-right-paragraph">
              <p>
                Ознайомтеся з графіком роботи та оберіть зручний час для знайомства з історією, культурою й унікальною спадщиною нашого краю.
              </p>
              <p>
                <strong>Вівторок – п’ятниця:</strong><br />
                Музей: 08:00–17:00<br />
                Екскурсії: 09:00–16:00
              </p>
              <p>
                <strong>Субота:</strong><br />
                Музей: 08:00–16:00<br />
                Екскурсії: 09:00–15:00
              </p>
              <p>
                Неділя та понеділок: вихідні дні.
              </p>
              <p>
                Обідня перерва: 12:00–13:00.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="kontakty" className="newsletter">
        <div className="left">
          <img src="/assets/img/m-288.jpg" alt="" />
        </div>
        <div className="right">
          <h3>Контакти</h3>
          <p style={{ fontWeight: 700, fontSize: '2.5rem' }}>Завжди раді вашим зверненням!</p>
          <p>Маєте запитання щодо роботи музею, плануєте візит або бажаєте замовити екскурсію? Зв’яжіться з нами — ми з радістю допоможемо!</p>
          <p>📞 Телефон: +380 (97) 814-79-45</p>
          <p>Графік роботи: вівторок – субота.</p>
          <p>Чекаємо на вас у Городоцькому краєзнавчому музеї G-Museum!</p>
        </div>
      </section>

      <Footer />
    </>
  );
}
